import React, { useState } from 'react';
import { withRouter } from 'react-router';
import dayjs from 'dayjs';
import { makeStyles, Grid, Typography, Button, Tooltip } from '@material-ui/core';
import Carousel from '../../reusable/Carousel';
import helpers from '../../utils/helpers';
import { useTools } from '../../hooks/useTools';

////////// COMPONENT //////////
function MoveDetailsLocation(props) {
  const cls = useStyles();

  const { type, move } = props;

  const { capFirst, getPickupOrDeliveryTimeFromMove, getMinutesBetween, checkMoveCompleted } = useTools();

  const [carousel, setCarousel] = useState(false);

  const isCompleted = checkMoveCompleted(move);

  const formatPickupStarted = dayjs(move.pickup_started).format(`h:mm A`);
  const formatPickupArrived = dayjs(move.pickup_arrived).format(`h:mm A`);
  const formatPickupSuccessful = dayjs(move.pickup_successful).format(`h:mm A`);
  const formatDeliveryStarted = dayjs(move.delivery_started).format(`h:mm A`);
  const formatDeliveryArrived = dayjs(move.delivery_arrived).format(`h:mm A`);
  const formatDeliverySuccessful = dayjs(move.delivery_successful).format(`h:mm A`);

  const pickupDrive = getMinutesBetween(move.pickup_started, move.pickup_arrived);
  const pickupInspect = getMinutesBetween(move.pickup_arrived, move.pickup_successful);
  const pickupDwell = getMinutesBetween(move.pickup_successful, move.delivery_started);
  const deliveryDrive = getMinutesBetween(move.delivery_started, move.delivery_arrived);
  const deliveryInspect = getMinutesBetween(move.delivery_arrived, move.delivery_successful);

  const pickupTotal = pickupDrive + pickupInspect + pickupDwell;
  const deliveryTotal = deliveryDrive + deliveryInspect;

  const totalTime = pickupDrive + pickupInspect + pickupDwell + deliveryDrive + deliveryInspect;

  const useBarStyles = makeStyles(theme => ({
    p1: {
      verticalAlign: 'top',
      position: 'relative',
      display: 'inline-block',
      width: `${pickupTotal && !isNaN(pickupTotal) ? (pickupDrive / pickupTotal) * 100 : 33.333}%`,
      height: theme.spacing(1),
      background: theme.palette.primary.main,
    },
    p2: {
      verticalAlign: 'top',
      position: 'relative',
      display: 'inline-block',
      width: `${pickupTotal && !isNaN(pickupTotal) ? (pickupInspect / pickupTotal) * 100 : 33.333}%`,
      height: theme.spacing(1),
      background: theme.palette.secondary.main,
    },
    p3: {
      verticalAlign: 'top',
      position: 'relative',
      display: 'inline-block',
      width: `${pickupTotal && !isNaN(pickupTotal) ? (pickupDwell / pickupTotal) * 100 : 33.333}%`,
      height: theme.spacing(1),
      background: theme.palette.secondary.light,
    },
    d1: {
      verticalAlign: 'top',
      position: 'relative',
      display: 'inline-block',
      width: `${deliveryTotal && !isNaN(deliveryTotal) ? (deliveryDrive / deliveryTotal) * 100 : 50}%`,
      height: theme.spacing(1),
      background: theme.palette.primary.main,
    },
    d2: {
      verticalAlign: 'top',
      position: 'relative',
      display: 'inline-block',
      width: `${deliveryTotal && !isNaN(deliveryTotal) ? (deliveryInspect / deliveryTotal) * 100 : 50}%`,
      height: theme.spacing(1),
      background: theme.palette.secondary.main,
    },
  }));
  const barCls = useBarStyles();

  try {
    return (
      <>
        <div className={cls.paper}>
          <Grid container spacing={0} alignItems='flex-start' wrap='nowrap'>
            <Grid item xs>
              <Tooltip placement='top-start' title={`${capFirst(type)} Location ID`}>
                <Typography className={cls.headTxtL}>
                  {capFirst(type)}&nbsp;#
                  {type === `pickup` ? move.lane.pickup.id : move.lane.delivery.id}
                </Typography>
              </Tooltip>
            </Grid>

            <Grid item>
              <Tooltip
                placement='top-end'
                title={`${isCompleted ? `Actual` : `Planned`} ${type === `pickup` ? `Start` : `End`} Time`}
              >
                <Typography className={cls.headTxtR}>{getPickupOrDeliveryTimeFromMove(type, move) || `N/A`}</Typography>
              </Tooltip>
            </Grid>
          </Grid>

          <div className={cls.smallBreak} />

          <Tooltip placement='top' title={`Click to see details for this location`}>
            <a
              href={`https://www.google.com/maps/dir/?api=1&travelmode=driving&layer=traffic&destination=${
                type === `pickup` ? move.lane.pickup.address : move.lane.delivery.address
              }`}
              noopener
              noreferrer
              target='_blank'
            >
              <div className={cls.location}>
                <Typography className={cls.nameTxt}>{helpers.handleNestedLocationObjects(move)[type].name}</Typography>
                <Typography className={cls.addressTxt}>
                  {helpers.handleNestedLocationObjects(move)[type].address}
                </Typography>
              </div>
            </a>
          </Tooltip>

          <div className={cls.midBreak} />

          <div className={cls.block}>
            {type === `pickup` ? (
              <>
                {pickupDrive || pickupTotal === 0 ? (
                  <Tooltip
                    title={`Pickup Drive - ${
                      pickupDrive ? `${pickupDrive} minutes (${formatPickupStarted} to ${formatPickupArrived})` : `0m`
                    }`}
                  >
                    <div className={barCls.p1} />
                  </Tooltip>
                ) : null}

                {pickupInspect || pickupTotal === 0 ? (
                  <Tooltip
                    title={`Pickup Inspection - ${
                      pickupInspect
                        ? `${pickupInspect} minutes (${formatPickupArrived} to ${formatPickupSuccessful})`
                        : `0m`
                    }`}
                  >
                    <div className={barCls.p2} />
                  </Tooltip>
                ) : null}

                {pickupDwell || pickupTotal === 0 ? (
                  <Tooltip
                    title={`Pickup Dwelling - ${
                      pickupDwell
                        ? `${pickupDwell} minutes (${formatPickupSuccessful} to ${formatDeliveryStarted})`
                        : `0m`
                    }`}
                  >
                    <div className={barCls.p3} />
                  </Tooltip>
                ) : null}
              </>
            ) : (
              <>
                {deliveryDrive || deliveryTotal === 0 ? (
                  <Tooltip
                    title={`Delivery Drive - ${
                      deliveryDrive
                        ? `${deliveryDrive} minutes (${formatDeliveryStarted} to ${formatDeliveryArrived})`
                        : `0m`
                    }`}
                  >
                    <div className={barCls.d1} />
                  </Tooltip>
                ) : null}

                {deliveryInspect || deliveryTotal === 0 ? (
                  <Tooltip
                    title={`Delivery Inspection - ${
                      deliveryInspect
                        ? `${deliveryInspect} minutes (${formatDeliveryArrived} to ${formatDeliverySuccessful})`
                        : `0m`
                    }`}
                  >
                    <div className={barCls.d2} />
                  </Tooltip>
                ) : null}
              </>
            )}
          </div>

          <Grid container spacing={0} justifyContent='space-between' wrap='nowrap'>
            {type === `pickup` ? (
              <>
                <Grid item xs={4} className={cls.leftTxt + ' ' + cls.ellipse}>
                  <Typography className={cls.timeTxtKey + ' ' + cls.inline}>Drive:&nbsp;</Typography>
                  <Typography className={cls.timeTxtVal + ' ' + cls.inline}>
                    {pickupDrive ? `${pickupDrive}m` : `0m`}
                  </Typography>
                </Grid>
                <Grid item xs={4} className={cls.midTxt + ' ' + cls.ellipse}>
                  <Typography className={cls.timeTxtKey + ' ' + cls.inline}>Inspect:&nbsp;</Typography>
                  <Typography className={cls.timeTxtVal + ' ' + cls.inline}>
                    {pickupInspect ? `${pickupInspect}m` : `0m`}
                  </Typography>
                </Grid>
                <Grid item xs={4} className={cls.rightTxt + ' ' + cls.ellipse}>
                  <Typography className={cls.timeTxtKey + ' ' + cls.inline}>Dwell:&nbsp;</Typography>
                  <Typography className={cls.timeTxtVal + ' ' + cls.inline}>
                    {pickupDwell ? `${pickupDwell}m` : `0m`}
                  </Typography>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={4} className={cls.leftTxt + ' ' + cls.ellipse}>
                  <Typography className={cls.timeTxtKey + ' ' + cls.inline}>Drive:&nbsp;</Typography>
                  <Typography className={cls.timeTxtVal + ' ' + cls.inline}>
                    {deliveryDrive ? `${deliveryDrive}m` : `0m`}
                  </Typography>
                </Grid>
                <Grid item xs={4} className={cls.midTxt + ' ' + cls.ellipse}>
                  <Typography className={cls.timeTxtKey + ' ' + cls.inline}>Inspect:&nbsp;</Typography>
                  <Typography className={cls.timeTxtVal + ' ' + cls.inline}>
                    {deliveryInspect ? `${deliveryInspect}m` : `0m`}
                  </Typography>
                </Grid>
                <Grid item xs={4} className={cls.rightTxt + ' ' + cls.ellipse}>
                  <Typography className={cls.timeTxtKey + ' ' + cls.inline}>Total:&nbsp;</Typography>
                  <Typography className={cls.timeTxtVal + ' ' + cls.inline}>
                    {totalTime ? `${totalTime}m` : `0m`}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>

          <div className={cls.break} />

          {/* <Button
            variant='outlined'
            size='large'
            style={{ width: '100%' }}
            onClick={() => setCarousel(true)}
          >{`View ${capFirst(type)} Images`}</Button> */}

          {/* {carousel && <Carousel images={tookan.images} setCarousel={setCarousel} />} */}

          {/* {tookan.signature ? (
            <>
              <div className={cls.break} />
              <Typography className={cls.headTxt}>{`${capFirst(type)} Signature`}</Typography>
              <div className={cls.smallBreak} />
              <Grid container spacing={2} wrap='nowrap'>
                <Grid item>
                  <Typography
                    className={cls.infoTxt}
                  >{`The ${type} signature represents the confirmation of the specified vehicle being ${
                    type === `pickup` ? `picked up at` : `delivered to`
                  } the correct location by the correct driver.`}</Typography>
                </Grid>
                <Grid item xs>
                  <div className={cls.sig}>
                    <img className={cls.sigImage} src={tookan.signature} alt={`${type} signature`} />
                    <Typography className={cls.sigTxt}>{tookan.name ? tookan.name : `N/A`}</Typography>
                  </div>
                </Grid>
              </Grid>
            </>
          ) : null} */}
        </div>
      </>
    );
  } catch (err) {
    return (
      <div className={cls.paper}>
        <Grid container spacing={0} alignItems='flex-start' wrap='nowrap'>
          <Grid item xs>
            <Typography className={cls.headTxtL}>{`${capFirst(type)} location data not available`}</Typography>
          </Grid>
        </Grid>
      </div>
    );
  }
}

////////// STYLES //////////
const useStyles = makeStyles(theme => ({
  paper: {
    position: 'relative',
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: theme.shape.paperRadius,
    background: theme.palette.background.paper,
    boxShadow: theme.shadow.main,
  },
  headTxtL: {
    lineHeight: 1.25,
    fontSize: '21px',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '16px',
    },
    cursor: 'default',
  },
  headTxtR: {
    lineHeight: 1.25,
    fontSize: '16px',
    fontWeight: 400,
    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '14px',
    },
    cursor: 'default',
  },
  location: {
    padding: theme.spacing(1),
    borderRadius: '4px',
    backgroundColor: '#00000000',
    '&:hover': {
      backgroundColor: '#00000012',
    },
    cursor: 'pointer',
    transition: '0.2s',
  },
  nameTxt: {
    lineHeight: 1.25,
    fontSize: '18px',
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '14px',
    },
  },
  addressTxt: {
    color: theme.palette.text.secondary,
    lineHeight: 1.25,
    fontSize: '14px',
    fontWeight: 400,
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '10px',
    },
  },
  timeTxtKey: {
    color: theme.palette.text.secondary,
    lineHeight: 1.25,
    fontSize: '12px',
    fontWeight: 400,
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '10px',
    },
  },
  timeTxtVal: {
    lineHeight: 1.25,
    fontSize: '12px',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '10px',
    },
  },
  infoTxt: {
    color: theme.palette.text.secondary,
    lineHeight: 1.25,
    fontSize: '16px',
    fontWeight: 400,
    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '14px',
    },
  },
  sig: {
    display: 'block',
    position: 'relative',
    height: 'fit-content',
  },
  sigImage: {
    display: 'block',
    position: 'relative',
    maxWidth: '200px',
    maxHeight: '200px',
  },
  sigTxt: {
    position: 'absolute',
    bottom: theme.spacing(1),
    right: theme.spacing(1.5),
    color: '#fff',
    lineHeight: 1.25,
    fontSize: '16px',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '14px',
    },
  },
  break: {
    width: '100%',
    height: theme.spacing(2),
  },
  smallBreak: {
    width: '100%',
    height: theme.spacing(1),
  },
  midBreak: {
    width: '100%',
    height: theme.spacing(1.5),
  },
  block: {
    display: 'block',
    height: theme.spacing(1.5),
  },
  inline: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  leftTxt: {
    textAlign: 'left',
  },
  midTxt: {
    textAlign: 'center',
  },
  rightTxt: {
    textAlign: 'right',
  },
  ellipse: {
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

////////// EXPORT //////////
export default withRouter(MoveDetailsLocation);
